import { FolderTree, Plus, Upload } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/button";
import { ConfirmationDialog } from "../../../components/confirmation-dialog";
import { useToast } from "../../../components/toast-container";
import {
  ErrorPlaceholder,
  NoDataPlaceholder,
  ScrollableContentLayout,
} from "../../../components/common";
import {
  CampaignFilters,
  CampaignHierarchyTable,
  CampaignImportDialog,
} from "../components";
import { useCampaignDashboard, useCampaignPageState } from "../hooks";
import { CampaignImportResponse } from "../types";
import { campaignStatIcons } from "../components/campaign.constants";
import { CAMPAIGN_STATUSES } from "../types/campaign-status.constants";
import { useAuthSession } from "../../auth/shared";

function CampaignPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user } = useAuthSession();
  const {
    data,
    isLoading: loading,
    isError,
    filters,
    dispatch,
    retry,
    deleteCampaign,
  } = useCampaignPageState();

  const [expandedCampaignId, setExpandedCampaignId] = useState<string | null>(null);
  const [expandedProgramId, setExpandedProgramId] = useState<string | null>(null);
  const [campaignToDelete, setCampaignToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const [isDeleting, setIsDeleting] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);

  const filteredCampaigns = useMemo(() => data?.campaigns ?? [], [data]);

  useEffect(() => {
    if (!filteredCampaigns.some((campaign) => campaign.id === expandedCampaignId)) {
      setExpandedCampaignId(null);
      setExpandedProgramId(null);
    }
  }, [filteredCampaigns, expandedCampaignId]);

  const dashboard = useCampaignDashboard(filters);
  const hasCampaigns = (data?.campaigns?.length ?? 0) > 0;
  const dashboardStats = useMemo(() => {
    const summary = dashboard.data?.summary;
    if (!summary) return [];

    return [
      {
        label: "Residents Engaged",
        value: summary.residentsEngaged.toLocaleString("en-US"),
        icon: campaignStatIcons.residentsEngaged,
      },
      {
        label: "Active Campaigns",
        value: summary.activeCampaignCount,
        icon: campaignStatIcons.activeCampaignCount,
      },
      {
        label: "Community Partners",
        value: summary.communityPartnerCount,
        icon: campaignStatIcons.communityPartnerCount,
      },
      {
        label: "Opportunities Completed",
        value: summary.opportunitiesCompletedCount,
        icon: campaignStatIcons.opportunitiesCompletedCount,
      },
    ];
  }, [dashboard.data]);

  const toggleCampaign = (campaignId: string) => {
    setExpandedCampaignId((current) => {
      const nextValue = current === campaignId ? null : campaignId;
      if (nextValue !== current) {
        setExpandedProgramId(null);
      }
      return nextValue;
    });
  };

  const toggleProgram = (programId: string) => {
    setExpandedProgramId((current) => (current === programId ? null : programId));
  };

  const openCampaignDetail = (campaignId: string) => {
    navigate(`/campaign/${campaignId}/edit`);
  };

  const openProgramDetail = (campaignId: string, programId: string) => {
    navigate(`/campaign/${campaignId}/program/edit`, {
      state: {
        selectedProgramId: programId,
      },
    });
  };

  const openOpportunityDetail = (campaignId: string, programId: string, opportunityId: string) => {
    navigate(`/campaign/${campaignId}/program/edit`, {
      state: { selectedProgramId: programId, selectedOpportunityId: opportunityId },
    });
  };

  const openProgramEditor = (campaignId: string) => {
    navigate(`/campaign/${campaignId}/program/edit`);
  };

  const handleDeleteCampaignClick = (campaignId: string) => {
    const campaign = filteredCampaigns.find((item) => item.id === campaignId);
    if (!campaign) {
      return;
    }

    setCampaignToDelete({
      id: campaign.id,
      name: campaign.name,
    });
  };

  const handleConfirmDeleteCampaign = async () => {
    if (!campaignToDelete) {
      return;
    }

    try {
      setIsDeleting(true);
      await deleteCampaign(campaignToDelete.id);
      addToast({
        type: "success",
        title: t("common.success"),
        message: t("campaign.delete_success"),
      });
      setCampaignToDelete(null);
    } catch (deleteError) {
      addToast({
        type: "error",
        title: t("campaign.delete_failed_title"),
        message: deleteError instanceof Error ? deleteError.message : t("campaign.delete_failed"),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <ScrollableContentLayout
      title={t("campaign.title")}
      subtitle={t("campaign.subtitle")}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="brand-outline"
            onClick={() => setIsImportOpen(true)}
            className="h-11 px-4"
          >
            <Upload className="mr-2 h-4 w-4" />
            {t("campaign.import.trigger")}
          </Button>
          <Button
            variant="primary"
            onClick={() => navigate("/campaign/create")}
            className="h-11 px-4"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t("campaign.create")}
          </Button>
        </div>
      }
      stats={dashboardStats}
      headerContent={
        <CampaignFilters
          loading={loading}
          searchTerm={filters.searchTerm}
          statusFilter={filters.statusFilter}
          onSearchChange={(value) => dispatch({ type: "filter/searchChanged", payload: value })}
          onStatusChange={(value) =>
            dispatch({
              type: "filter/statusChanged",
              payload: value === "all" ? "all" : (value as CAMPAIGN_STATUSES),
            })
          }
          onRetry={() => void retry()}
        />
      }
      loading={dashboard.isLoading}
      statLabels={[
        { label: "Residents Engaged", icon: campaignStatIcons.residentsEngaged },
        { label: "Active Campaigns", icon: campaignStatIcons.activeCampaignCount },
        { label: "Community Partners", icon: campaignStatIcons.communityPartnerCount },
        {
          label: "Opportunities Completed",
          icon: campaignStatIcons.opportunitiesCompletedCount,
        },
      ]}
    >
      {isError && !loading ? (
        <ErrorPlaceholder
          title={t("campaign.load_failed")}
          description={t("campaign.load_failed_description")}
          onRetry={() => void retry()}
        />
      ) : null}

      {!isError && !loading && !hasCampaigns ? (
        <NoDataPlaceholder
          icon={FolderTree}
          title={t("campaign.empty_title")}
          description={t("campaign.empty_description")}
        />
      ) : null}

      {!isError && hasCampaigns ? (
        <CampaignHierarchyTable
          campaigns={filteredCampaigns}
          expandedCampaignId={expandedCampaignId}
          expandedProgramId={expandedProgramId}
          onToggleCampaign={toggleCampaign}
          onToggleProgram={toggleProgram}
          onOpenCampaignDetail={openCampaignDetail}
          onOpenProgramEditor={openProgramEditor}
          onOpenProgramDetail={openProgramDetail}
          onOpenOpportunityDetail={openOpportunityDetail}
          onDeleteCampaign={handleDeleteCampaignClick}
        />
      ) : null}

      <ConfirmationDialog
        isOpen={Boolean(campaignToDelete)}
        onClose={() => {
          if (!isDeleting) {
            setCampaignToDelete(null);
          }
        }}
        onConfirm={() => void handleConfirmDeleteCampaign()}
        title={t("campaign.delete_title")}
        description={
          campaignToDelete ? t("campaign.delete_confirmation", { name: campaignToDelete.name }) : ""
        }
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        isLoading={isDeleting}
        type="danger"
      />
      <CampaignImportDialog
        open={isImportOpen}
        currentOrganizationId={user?.organization_id}
        onOpenChange={setIsImportOpen}
        onImported={async (result: CampaignImportResponse) => {
          await retry();
          addToast({
            type: "success",
            title: t("campaign.import.toast_title"),
            message: t("campaign.import.toast_message", {
              campaigns: result.campaignsCreated,
              programs: result.programsCreated,
              opportunities: result.opportunitiesCreated,
            }),
          });
        }}
      />
    </ScrollableContentLayout>
  );
}

export default CampaignPage;
